/**
 * Aircraft state / flight-phase contract.
 *
 * Single source of truth for Dashboard + Map progress bars, Inbox / Grok
 * filtering, and the assignment engine. Derived automatically from fused
 * SimConnect / position samples — never from a pilot “I’m taxiing” click.
 *
 * Phase IDs come from {@link deriveFlightPhase} / {@link aircraftSimState}
 * (PR #42). This module adds nearest/current field for assignment + Grok.
 *
 * Contract (sample fields this derivation reads):
 *   lat, lon, heading, altitudeFt, altitudeAgl, airspeed, onGround, atUtc
 * Optional (native SimConnect / richer HTTP stubs — ignored if missing):
 *   groundSpeedKt, verticalSpeedFpm
 *
 * Never invent progress. Offline / stale last-known with no live sim → `unknown`.
 */
import { isNarniaGroundSpawn } from "./flightBinding";
import { aircraftSimState, type FlightPhaseId } from "./flightPhase";
import { haversineNm } from "./geo";
import { nearestDestination, type FusedPosition, type PositionSample } from "./position";
import type { AircraftSpec, Mission, SceneryAirport } from "./types";

export const AIRCRAFT_PHASES = [
  "unknown",
  "on_ground",
  "taxi",
  "takeoff_climb",
  "cruise",
  "descent",
  "approach",
  "landed",
] as const;

export type AircraftPhase = (typeof AIRCRAFT_PHASES)[number];

function mapFlightPhase(id: FlightPhaseId): AircraftPhase {
  if (id === "no_sim" || id === "unknown") return "unknown";
  if (id === "takeoff" || id === "climb") return "takeoff_climb";
  if (id === "parked") return "landed";
  return id;
}

function asFused(raw: PositionSample | FusedPosition, live?: boolean): FusedPosition {
  const fused = raw as FusedPosition;
  const source =
    fused.source === "simconnect" ||
    fused.source === "sayintentions" ||
    fused.source === "vatsim" ||
    fused.source === "last_known"
      ? fused.source
      : "simconnect";
  return {
    lat: raw.lat,
    lon: raw.lon,
    heading: raw.heading,
    altitudeFt: raw.altitudeFt,
    altitudeAgl: raw.altitudeAgl,
    airspeed: raw.airspeed,
    groundSpeed: fused.groundSpeed,
    verticalSpeedFpm: fused.verticalSpeedFpm,
    onGround: raw.onGround,
    atUtc: raw.atUtc,
    gearDown: typeof raw.gearDown === "boolean" ? raw.gearDown : null,
    source,
    stale: fused.stale === true || live === false,
  };
}

/** Ordered set for the progress bar (unknown is off-bar). */
export const AIRCRAFT_PHASE_BAR: Exclude<AircraftPhase, "unknown">[] = [
  "on_ground",
  "taxi",
  "takeoff_climb",
  "cruise",
  "descent",
  "approach",
  "landed",
];

export const AIRCRAFT_PHASE_LABEL: Record<AircraftPhase, string> = {
  unknown: "No sim",
  on_ground: "On ground",
  taxi: "Taxi",
  takeoff_climb: "Takeoff/Climb",
  cruise: "Cruise/In flight",
  descent: "Descent",
  approach: "Approach",
  landed: "Landed/Arrived",
};

export type AircraftState = {
  phase: AircraftPhase;
  label: string;
  source: FusedPosition["source"] | "none";
  stale: boolean;
  /** Nearest Destinations field when coords exist. */
  nearestIcao: string | null;
  nearestNm: number | null;
  /** Last parked / filed field when no live nearest. */
  parkedIcao: string | null;
  /** Best current-field guess for assignment (nearest, then parked). */
  currentIcao: string | null;
  note: string;
  atUtc: string | null;
  sample: PositionSample | null;
};

export type TailAircraftState = AircraftState & {
  aircraftId: string;
  registration: string;
  /** True when this tail is the live fused sample (single-active desk). */
  live: boolean;
};

export type DeriveAircraftStateInput = {
  sample?: PositionSample | FusedPosition | null;
  previous?: PositionSample | null;
  /** Destinations list for nearest-field. */
  scenery?: SceneryAirport[];
  /** Parked / last-filed field when the sample has no nearest. */
  parkedIcao?: string | null;
  /** Assigned / active trip — used only to label approach/landed, not to invent phase. */
  mission?: Pick<Mission, "originIcao" | "destIcao" | "status"> | null;
  now?: Date;
  /** Fresh-sample window. Stale last-known without a live source stays unknown. */
  live?: boolean;
};

const TAXI_KT = 5;
const TAKEOFF_ROLL_KT = 40;
const CRUISE_ALT_FT = 8000;
const APPROACH_AGL_FT = 2500;
const APPROACH_NEAR_NM = 25;
const LAND_NEAR_NM = 4;
const CLIMB_VS = 400;
const DESCEND_VS = -300;

function asFinite(n: unknown): number | null {
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

function sampleAgeMs(sample: Pick<PositionSample, "atUtc"> | null | undefined, now: Date): number {
  if (!sample?.atUtc) return Number.POSITIVE_INFINITY;
  const t = new Date(sample.atUtc).getTime();
  if (Number.isNaN(t)) return Number.POSITIVE_INFINITY;
  return Math.max(0, now.getTime() - t);
}

function verticalSpeedFpm(sample: PositionSample, previous?: PositionSample | null): number | null {
  const direct = asFinite((sample as PositionSample & { verticalSpeedFpm?: number | null }).verticalSpeedFpm);
  if (direct != null) return direct;
  if (!previous || previous.altitudeFt == null || sample.altitudeFt == null) return null;
  const dtMin = (new Date(sample.atUtc).getTime() - new Date(previous.atUtc).getTime()) / 60_000;
  if (!(dtMin > 0.05) || dtMin > 8) return null;
  return (sample.altitudeFt - previous.altitudeFt) / dtMin;
}

function groundSpeedKt(sample: PositionSample, previous?: PositionSample | null): number | null {
  const gs = asFinite((sample as PositionSample & { groundSpeedKt?: number | null }).groundSpeedKt);
  if (gs != null) return gs;
  if (sample.airspeed != null) return sample.airspeed;
  if (!previous) return null;
  const dtHr = (new Date(sample.atUtc).getTime() - new Date(previous.atUtc).getTime()) / 3_600_000;
  if (!(dtHr > 0.0005) || dtHr > 0.25) return null;
  return haversineNm(previous.lat, previous.lon, sample.lat, sample.lon) / dtHr;
}

function wasAirborne(previous?: PositionSample | null): boolean {
  if (!previous) return false;
  if (previous.onGround === false) return true;
  if (previous.onGround === true) return false;
  return (previous.altitudeAgl ?? previous.altitudeFt ?? 0) > 800;
}

export function isAirbornePhase(phase: AircraftPhase): boolean {
  return phase === "takeoff_climb" || phase === "cruise" || phase === "descent" || phase === "approach";
}

export function isGroundPhase(phase: AircraftPhase): boolean {
  return phase === "on_ground" || phase === "taxi" || phase === "landed";
}

export function phaseBarIndex(phase: AircraftPhase): number {
  return AIRCRAFT_PHASE_BAR.indexOf(phase as Exclude<AircraftPhase, "unknown">);
}

export function emptyAircraftState(now = new Date()): AircraftState {
  return {
    phase: "unknown",
    label: AIRCRAFT_PHASE_LABEL.unknown,
    source: "none",
    stale: true,
    nearestIcao: null,
    nearestNm: null,
    parkedIcao: null,
    currentIcao: null,
    note: "No live sim sample. Assignment uses last-known / parked field only.",
    atUtc: now.toISOString(),
    sample: null,
  };
}

function derivePhase(input: {
  sample: PositionSample;
  previous?: PositionSample | null;
  destIcao?: string | null;
  nearestIcao?: string | null;
  nearestNm?: number | null;
}): { phase: AircraftPhase; note: string } {
  const { sample, previous } = input;
  const vs = verticalSpeedFpm(sample, previous);
  const gs = groundSpeedKt(sample, previous);
  const alt = sample.altitudeFt;
  const agl = sample.altitudeAgl;
  const dest = input.destIcao?.toUpperCase() ?? null;
  const nearDest = Boolean(
    dest && input.nearestIcao && dest === input.nearestIcao.toUpperCase() && (input.nearestNm ?? 99) <= APPROACH_NEAR_NM,
  );
  const atDest = Boolean(
    dest && input.nearestIcao && dest === input.nearestIcao.toUpperCase() && (input.nearestNm ?? 99) <= LAND_NEAR_NM,
  );

  if (sample.onGround === true) {
    if (wasAirborne(previous) && (atDest || (gs != null && gs < TAXI_KT))) {
      return { phase: "landed", note: "On ground after an airborne sample." };
    }
    if (gs != null && gs >= TAKEOFF_ROLL_KT) {
      return { phase: "takeoff_climb", note: "On-ground roll above takeoff speed." };
    }
    if (gs != null && gs >= TAXI_KT) {
      return { phase: "taxi", note: "On ground with taxi speed." };
    }
    if (wasAirborne(previous)) return { phase: "landed", note: "On ground after airborne; speed unknown." };
    return { phase: "on_ground", note: "On ground, not moving." };
  }

  if (sample.onGround === false) {
    if ((agl != null && agl < APPROACH_AGL_FT && (vs == null || vs < -150) && nearDest) || (nearDest && vs != null && vs < DESCEND_VS)) {
      return { phase: "approach", note: "Airborne, low, descending toward destination." };
    }
    if (vs != null && vs < DESCEND_VS && (alt == null || alt > 2500)) {
      return { phase: "descent", note: "Airborne with a descent rate." };
    }
    if (vs != null && vs > CLIMB_VS && (alt == null || alt < 18_000)) {
      return { phase: "takeoff_climb", note: "Airborne with a climb rate." };
    }
    if (alt != null && alt >= CRUISE_ALT_FT && (vs == null || Math.abs(vs) < 400)) {
      return { phase: "cruise", note: "Airborne at cruise altitude." };
    }
    if (alt != null && alt >= 3000) {
      return { phase: "cruise", note: "Airborne — in flight." };
    }
    if (vs != null && vs > 200) return { phase: "takeoff_climb", note: "Low airborne climb." };
    if (vs != null && vs < -150) return { phase: "descent", note: "Low airborne descent." };
    return { phase: "cruise", note: "Airborne; rate/altitude inconclusive — in flight." };
  }

  if (alt != null && alt >= CRUISE_ALT_FT && (gs == null || gs > 80)) {
    return { phase: "cruise", note: "Altitude looks airborne (onGround unknown)." };
  }
  if ((agl != null && agl < 50 && (gs == null || gs < TAXI_KT)) || (alt != null && alt < 100 && (gs == null || gs < TAXI_KT))) {
    return { phase: "on_ground", note: "Low/stationary; onGround unset." };
  }
  if (gs != null && gs >= TAXI_KT && gs < TAKEOFF_ROLL_KT && (alt == null || alt < 200)) {
    return { phase: "taxi", note: "Taxi-range speed; onGround unset." };
  }
  return { phase: "unknown", note: "Sample is inconclusive. Not inventing a phase." };
}

export function deriveAircraftState(input: DeriveAircraftStateInput = {}): AircraftState {
  const now = input.now ?? new Date();
  const raw = input.sample ?? null;
  if (!raw || !Number.isFinite(raw.lat) || !Number.isFinite(raw.lon)) {
    const parked = input.parkedIcao?.trim().toUpperCase() || null;
    return {
      ...emptyAircraftState(now),
      parkedIcao: parked,
      currentIcao: parked,
    };
  }

  const fused = raw as FusedPosition;
  const source =
    fused.source === "simconnect" ||
    fused.source === "sayintentions" ||
    fused.source === "vatsim" ||
    fused.source === "last_known"
      ? fused.source
      : "none";
  const live = input.live !== false && source !== "last_known" && fused.stale !== true;
  if (!live) {
    const parked = input.parkedIcao?.trim().toUpperCase() || null;
    const near = input.scenery?.length ? nearestDestination(raw, input.scenery, 180) : null;
    return {
      phase: "unknown",
      label: AIRCRAFT_PHASE_LABEL.unknown,
      source,
      stale: true,
      nearestIcao: near?.airport.icao ?? null,
      nearestNm: near?.distanceNm ?? null,
      parkedIcao: parked,
      currentIcao: parked ?? near?.airport.icao ?? null,
      note: "No live sim — last-known / parked only. Progress bar stays unknown.",
      atUtc: raw.atUtc,
      sample: raw,
    };
  }

  const near = input.scenery?.length ? nearestDestination(raw, input.scenery, 180) : null;
  const parked = input.parkedIcao?.trim().toUpperCase() || null;
  const currentIcao =
    parked && isNarniaGroundSpawn(asFused(raw, true), parked, input.scenery)
      ? parked
      : (near?.airport.icao ?? parked);
  const prevPhase = input.previous
    ? aircraftSimState({ fused: asFused(input.previous), now }).phase
    : null;
  const sim = aircraftSimState({
    fused: asFused(raw, true),
    previous: prevPhase === "no_sim" || prevPhase === "unknown" ? null : prevPhase,
    now,
  });
  const phase = mapFlightPhase(sim.phase);

  return {
    phase,
    label: AIRCRAFT_PHASE_LABEL[phase],
    source,
    stale: false,
    nearestIcao: near?.airport.icao ?? null,
    nearestNm: near != null ? near.distanceNm : null,
    parkedIcao: parked,
    currentIcao,
    note: sim.note,
    atUtc: raw.atUtc || now.toISOString(),
    sample: raw,
  };
}

export function hydrateAircraftState(raw?: Partial<AircraftState> | null): AircraftState | null {
  if (!raw || typeof raw !== "object") return null;
  const phase = AIRCRAFT_PHASES.includes(raw.phase as AircraftPhase) ? (raw.phase as AircraftPhase) : "unknown";
  return {
    phase,
    label: raw.label?.trim() || AIRCRAFT_PHASE_LABEL[phase],
    source:
      raw.source === "simconnect" ||
      raw.source === "sayintentions" ||
      raw.source === "vatsim" ||
      raw.source === "last_known"
        ? raw.source
        : "none",
    stale: raw.stale !== false,
    nearestIcao: raw.nearestIcao?.trim().toUpperCase() || null,
    nearestNm: typeof raw.nearestNm === "number" && Number.isFinite(raw.nearestNm) ? raw.nearestNm : null,
    parkedIcao: raw.parkedIcao?.trim().toUpperCase() || null,
    currentIcao: raw.currentIcao?.trim().toUpperCase() || raw.nearestIcao?.trim().toUpperCase() || raw.parkedIcao?.trim().toUpperCase() || null,
    note: raw.note ?? "",
    atUtc: typeof raw.atUtc === "string" ? raw.atUtc : null,
    sample: raw.sample ?? null,
  };
}

/**
 * Per-tail hook. Single-active desk: the live fused sample belongs to `activeAircraftId`
 * (SI registration / last assigned). Other tails stay parked at lastAirportIcao.
 * Does not invent a multi-fleet product.
 */
export function statesForFleet(input: {
  fleet: AircraftSpec[];
  live: AircraftState;
  activeAircraftId?: string | null;
  activeRegistration?: string | null;
}): TailAircraftState[] {
  const activeId = input.activeAircraftId?.trim();
  const activeReg = input.activeRegistration?.trim().toUpperCase();
  return input.fleet.map((spec) => {
    const live =
      (activeId && spec.id === activeId) ||
      (activeReg != null && spec.registration.toUpperCase() === activeReg);
    if (live) {
      return {
        ...input.live,
        aircraftId: spec.id,
        registration: spec.registration,
        parkedIcao: spec.lastAirportIcao?.toUpperCase() ?? input.live.parkedIcao,
        currentIcao: input.live.currentIcao ?? spec.lastAirportIcao?.toUpperCase() ?? null,
        live: true,
      };
    }
    const parked = spec.lastAirportIcao?.toUpperCase() ?? null;
    return {
      ...emptyAircraftState(),
      aircraftId: spec.id,
      registration: spec.registration,
      parkedIcao: parked,
      currentIcao: parked,
      note: "No live sample for this tail — parked field only.",
      live: false,
    };
  });
}

/** Clamp text for Grok / Inbox prompts. Honest when unknown. */
export function aircraftStatePromptLine(state: AircraftState | null | undefined): string {
  if (!state || state.phase === "unknown") {
    return "Aircraft state: unknown / no sim. Do not invent a phase or fake progress. Prefer later STDs. Customer RFQ origin is a current company base or the tail's assigned base — not a leftover CYPD pad, and not last-known field unless that field is a current base (ops uses position for assign / ferry).";
  }
  const field = state.currentIcao ? ` current field ${state.currentIcao}` : "";
  const near = state.nearestIcao && state.nearestNm != null ? ` (${state.nearestIcao} ${state.nearestNm.toFixed(0)} NM)` : "";
  return `Aircraft state (automatic, not pilot-reported): ${state.label}${field}${near}. ${state.note} Occasional overlap is OK — do not propose an immediate departure the aircraft cannot make from this state. Inbox RFQ origin stays a current company base; current field is for assignment, not the customer origin, unless that field is a current base.`;
}

export function activeMissionForPhase(
  missions: Mission[],
  registration?: string | null,
): Mission | null {
  const open = missions.filter((m) => m.status === "pending" || m.status === "in-progress");
  if (registration) {
    const hit = open.find((m) => m.registration.toUpperCase() === registration.toUpperCase());
    if (hit) return hit;
  }
  return open.find((m) => m.status === "in-progress") ?? open[0] ?? null;
}

export function phaseRouteLabel(mission: Pick<Mission, "originIcao" | "destIcao"> | null | undefined): string {
  if (!mission) return "";
  return `${mission.originIcao}→${mission.destIcao}`;
}

/** Inputs the solo flight-progress strip consumes when a trip is assigned / active. */
export type PhaseStripFeed = {
  route: string;
  originIcao: string;
  destIcao: string;
  etaUtc: string | null;
  etaLabel: string;
  registration: string;
  icaoType: string;
  flightType: string;
  crewId: string;
  crewName: string;
};

export function missionEtaUtc(mission: Pick<Mission, "departureIso" | "eteMin">): string | null {
  const start = new Date(mission.departureIso).getTime();
  if (!Number.isFinite(start)) return null;
  return new Date(start + Math.max(0, mission.eteMin) * 60_000).toISOString();
}

export function formatEtaLabel(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso);
  if (!Number.isFinite(t.getTime())) return "—";
  return `${String(t.getUTCHours()).padStart(2, "0")}:${String(t.getUTCMinutes()).padStart(2, "0")}Z`;
}

export function buildPhaseStripFeed(input: {
  mission?: Pick<Mission, "originIcao" | "destIcao" | "departureIso" | "eteMin" | "registration" | "flightType"> | null;
  assignment?: { crewId?: string; crewName?: string; registration?: string } | null;
  icaoType?: string;
}): PhaseStripFeed | null {
  const mission = input.mission;
  if (!mission?.originIcao || !mission.destIcao) return null;
  const etaUtc = missionEtaUtc(mission);
  return {
    route: phaseRouteLabel(mission),
    originIcao: mission.originIcao,
    destIcao: mission.destIcao,
    etaUtc,
    etaLabel: formatEtaLabel(etaUtc),
    registration: (input.assignment?.registration || mission.registration || "").toUpperCase(),
    icaoType: (input.icaoType ?? "").toUpperCase(),
    flightType: mission.flightType ?? "",
    crewId: input.assignment?.crewId ?? "",
    crewName: input.assignment?.crewName ?? "",
  };
}
